/**
 * Utility functions for handling HTML content in podcast descriptions
 */

/**
 * Strips HTML tags from a string and returns plain text.
 * Useful for previews and list items where we don't want to render HTML.
 */
export function stripHtmlTags(html: string | null | undefined): string {
    if (!html || typeof html !== 'string') return '';
    
    try {
        // Create a temporary element to parse HTML
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
    } catch {
        // Fallback: simple regex strip if DOMParser fails
        return html.replace(/<[^>]*>/g, '');
    }
}

/**
 * Sanitizes HTML content for safe rendering using dangerouslySetInnerHTML.
 * Only allows safe tags and removes potentially dangerous content.
 */
export function sanitizeHtml(html: string | null | undefined): string {
    if (!html || typeof html !== 'string') return '';
    
    try {
        // List of allowed tags
        const allowedTags = ['p', 'br', 'b', 'i', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'span'];
        
        // Create a temporary element
        const doc = new DOMParser().parseFromString(html, 'text/html');
    
    // Function to recursively clean nodes
    const cleanNode = (node: Node): Node | null => {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.cloneNode();
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            const tagName = element.tagName.toLowerCase();
            
            // Check if tag is allowed
            if (!allowedTags.includes(tagName)) {
                // Return text content only for disallowed tags
                const fragment = document.createDocumentFragment();
                element.childNodes.forEach(child => {
                    const cleaned = cleanNode(child);
                    if (cleaned) fragment.appendChild(cleaned);
                });
                return fragment;
            }
            
            // Create new element with allowed tag
            const newElement = document.createElement(tagName);
            
            // Only allow href for anchor tags
            if (tagName === 'a') {
                const href = element.getAttribute('href');
                if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
                    newElement.setAttribute('href', href);
                    newElement.setAttribute('target', '_blank');
                    newElement.setAttribute('rel', 'noopener noreferrer');
                }
            }
            
            // Recursively clean children
            element.childNodes.forEach(child => {
                const cleaned = cleanNode(child);
                if (cleaned) newElement.appendChild(cleaned);
            });
            
            return newElement;
        }
        
        return null;
    };
    
    const fragment = document.createDocumentFragment();
    doc.body.childNodes.forEach(child => {
        const cleaned = cleanNode(child);
        if (cleaned) fragment.appendChild(cleaned);
    });
    
    // Convert fragment to HTML string
    const container = document.createElement('div');
    container.appendChild(fragment);
    return container.innerHTML;
    } catch {
        // Fallback: return escaped text if sanitization fails
        return html.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
}
